// src/sectors/pilates/reports/staff/tabs/OverviewTab.jsx
import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { StaffFiltersCard } from '../components/StaffFiltersCard';
import { endOfMonth, startOfMonth, toISODateInput } from '../utils/staffReportFormatters';

import { useTrainerOverview } from '../overview/hooks/useTrainerOverview';

import { StaffOverviewKpiRow } from '../overview/ui/StaffOverviewKpiRow';
import { StaffOverviewChartsRow } from '../overview/ui/StaffOverviewChartsRow';
import { StaffOverviewActionListsRow } from '../overview/ui/StaffOverviewActionListsRow';

function pickStaffId(s) {
  return s?.staffId ?? s?.id ?? null;
}

function SectionTitle({ children, right }) {
  return (
    <MDBox mb={1.5} display="flex" alignItems="center" justifyContent="space-between">
      <MDTypography variant="h6" sx={{ fontSize: 14, opacity: 0.85 }}>
        {children}
      </MDTypography>
      {right ? <MDBox>{right}</MDBox> : null}
    </MDBox>
  );
}

export default function OverviewTab() {
  const [from, setFrom] = useState(startOfMonth(new Date()));
  const [to, setTo] = useState(endOfMonth(new Date()));
  const [selectedStaff, setSelectedStaff] = useState(null);

  const trainerId = pickStaffId(selectedStaff);
  const fromStr = toISODateInput(from);
  const toStr = toISODateInput(to);

  const q = useTrainerOverview({ trainerId, from: fromStr, to: toStr });

  // ✅ BE overview payload
  const agg = q.data ?? {};
  const loading = q.isLoading || q.isFetching;

  const ratingRaw = Number(agg?.rating ?? agg?.attendPct ?? 0);
  const rating = Number.isFinite(ratingRaw) ? ratingRaw : 0;
  const ratingColor = rating >= 8 ? 'success' : rating >= 5 ? 'warning' : 'error';
  const ratingLabel = rating.toFixed(1);

  return (
    <MDBox py={{ xs: 2.25, md: 3 }}>
      {/* Filters */}
      <MDBox>
        <StaffFiltersCard
          from={from}
          to={to}
          selectedStaff={selectedStaff}
          onSelectStaff={setSelectedStaff}
          loading={loading}
          onChangeFrom={setFrom}
          onChangeTo={setTo}
          onList={() => q.refetch()}
        />

        <MDBox mt={1.5} display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {!trainerId ? (
            <Chip label="Eğitmen seç" variant="outlined" />
          ) : q.isError ? (
            <Chip label="⚠️ Rapor çekilemedi" color="error" variant="outlined" />
          ) : (
            <Chip
              label={q.isFetching ? 'Yükleniyor…' : 'Hazır'}
              color={q.isFetching ? 'default' : 'success'}
              variant="outlined"
            />
          )}

          <Chip
            label={`${fromStr || '—'} → ${toStr || '—'}`}
            variant="outlined"
            sx={{ opacity: 0.85 }}
          />
        </MDBox>
      </MDBox>

      {/* Genel Bakış */}
      <MDBox mt={4.5}>
        <SectionTitle
          right={
            <Chip
              icon={<Icon>star</Icon>}
              label={loading || !trainerId ? '—' : ratingLabel}
              color={loading || !trainerId ? 'default' : ratingColor}
              variant="outlined"
              size="small"
              sx={{ borderRadius: 999, fontWeight: 700, letterSpacing: 0.2 }}
            />
          }
        >
          Genel Bakış
        </SectionTitle>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StaffOverviewKpiRow trainerId={trainerId} loading={loading} agg={agg} />
          </Grid>
        </Grid>
      </MDBox>

      {/* Grafikler */}
      <MDBox mt={4.5}>
        <SectionTitle>Grafikler</SectionTitle>
        <MDBox sx={{ pt: 1.5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StaffOverviewChartsRow trainerId={trainerId} loading={loading} agg={agg} />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>

      {/* Aksiyon Listeleri */}
      <MDBox mt={4.5}>
        <SectionTitle>Aksiyon Listeleri</SectionTitle>
        <MDBox sx={{ pt: 0.5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StaffOverviewActionListsRow trainerId={trainerId} agg={agg} />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
